import React from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ChargingActiveIcon } from '../../../assets/icons/charging-icon-active.svg';
import { ReactComponent as ChargingIcon } from '../../../assets/icons/charging-icon.svg';
import { ReactComponent as InvoiceIcon } from '../../../assets/icons/invoice-icon.svg';
import { ReactComponent as PaymentIcon } from '../../../assets/icons/payment-icon.svg';
import { useAppBranding } from '../../../config/branding';

type StageStatus = {
  inProgress: boolean;
  done: boolean;
};

type FormStageState = {
  paymentDetails: StageStatus;
  charging: StageStatus;
  invoice: StageStatus;
};

interface ChargingStageProperties {
  formStage: FormStageState;
}

/**
 * Charging Stage and an active stage indicator
 * @param properties - The component properties.
 * @returns The rendered charging stage indicator element.
 */
function ChargingStage(properties: ChargingStageProperties) {
  const { t } = useTranslation('charger');
  const { formStage } = properties;
  const { images: brandImage } = useAppBranding();

  /**
   * Function to return width of the charging stage progress bar
   * @returns Percentage string representing the progress bar width.
   */
  const getWidthPercentage = () => {
    let widthToReturn = 0;
    if (formStage.paymentDetails.inProgress) {
      widthToReturn = 20;
    } else if (formStage.charging.inProgress) {
      widthToReturn = 50;
    } else if (formStage.invoice.inProgress) {
      widthToReturn = 80;
    }
    return `${widthToReturn}%`;
  };

  return (
    <div className="charging-stage-wrap">
      <div className="progress-bar-wrap">
        <div className="progress-bar" style={{ width: getWidthPercentage() }} />
      </div>
      <div
        className={`charging-stage-container ${formStage.paymentDetails.inProgress ? 'in-progress' : ''} ${formStage.paymentDetails.done ? 'done' : ''}`}>
        <img src={brandImage.currentForm} alt="" className="charging-active-image" />
        <div className="charging-stage">
          <span>
            <PaymentIcon />
          </span>
          <p>{t('paymentDetails')}</p>
        </div>
      </div>
      <div
        className={`charging-stage-container ${formStage.charging.inProgress ? 'in-progress' : ''} ${formStage.charging.done ? 'done' : ''}`}>
        <img src={brandImage.currentForm} alt="" className="charging-active-image" />
        <div className="charging-stage">
          <span>
            {formStage.charging.inProgress || formStage.charging.done ? (
              <ChargingActiveIcon />
            ) : (
              <ChargingIcon />
            )}
          </span>
          <p>{t('charging')}</p>
        </div>
      </div>
      <div
        className={`charging-stage-container ${formStage.invoice.inProgress ? 'in-progress' : ''} ${formStage.invoice.done ? 'done' : ''}`}>
        <img src={brandImage.currentForm} alt="" className="charging-active-image" />
        <div className="charging-stage">
          <span>
            <InvoiceIcon />
          </span>
          <p>{t('invoice')}</p>
        </div>
      </div>
    </div>
  );
}

export default ChargingStage;
